import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';
import RoleIcon from '../components/RoleIcon';

const ROLES = ['admin', 'doctor', 'technician', 'viewer'];
const LOG_PAGE_SIZE = 50;

// Sample dataset served by GET /files/demo/{name} (see backend DEMO_FILES).
// Sizes are approximate and shown for download expectations only.
const DEMO_DOWNLOADS = [
  { name: 'demo-mri-axial-dicoms.zip', title: 'Axial MRI — DICOM folder (zip)', size: '~8 MB', slot: 'MRI › Axial View (unzip first)' },
  { name: 'demo-mri-sagittal-dicoms.zip', title: 'Sagittal MRI — DICOM folder (zip)', size: '~3 MB', slot: 'MRI › Sagittal View (unzip first)' },
  { name: 'demo-ct-scan.nii.gz', title: 'CT scan — NIfTI volume', size: '~58 MB', slot: 'CT Scan › Full Scan' },
  { name: 'demo-xray-ap.jpg', title: 'X-ray — anteroposterior view', size: '<1 MB', slot: 'X-Ray › Anteroposterior View' },
  { name: 'demo-xray-lat.jpg', title: 'X-ray — lateral view', size: '~5 MB', slot: 'X-Ray › Lateral View' },
];

const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : 'Never');
const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

// The global error handler emits {error, code, message}; plain FastAPI
// HTTPExceptions are {detail: string} (or an array for validation errors)
const readError = async (response) => {
  try {
    const data = await response.json();
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.detail === 'string') return data.detail;
  } catch (e) {
    // non-JSON body, fall through
  }
  return `Request failed (${response.status})`;
};

const condenseLog = (log) => {
  const parts = [];
  if (log.resource_type) {
    parts.push(log.resource_id
      ? `${log.resource_type} ${String(log.resource_id).slice(0, 8)}`
      : log.resource_type);
  }
  if (log.details) {
    const d = String(log.details);
    parts.push(d.length > 80 ? `${d.slice(0, 80)}…` : d);
  }
  if (log.status === 'failure' && log.error_message) {
    parts.push(log.error_message);
  }
  return parts.join(' — ') || '—';
};

function SettingsPage() {
  const { user, getAuthHeaders } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') || 'account';
  const adminTabs = ['users', 'access', 'activity'];
  const activeTab = (adminTabs.includes(requestedTab) && !isAdmin) ? 'account' : requestedTab;

  const setActiveTab = (tab) => {
    setSearchParams(tab === 'account' ? {} : { tab }, { replace: true });
  };

  // ---- Account state ----
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ email: '', full_name: '', organization: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [changingPw, setChangingPw] = useState(false);

  // ---- Admin state ----
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  const [whitelist, setWhitelist] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [confirm, setConfirm] = useState(null);
  const messageTimer = useRef(null);

  useEffect(() => () => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
  }, []);

  const showSuccess = (text) => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
    setMessage({ type: 'success', text });
    messageTimer.current = setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const showError = (text) => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
    setMessage({ type: 'error', text });
  };

  const apiGet = async (path) => {
    const response = await fetch(`${API_URL}${path}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  };

  const apiSend = async (path, { method = 'POST', body = null } = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
      method,
      headers: {
        ...getAuthHeaders(),
        ...(body ? { 'Content-Type': 'application/json' } : {})
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  };

  // ---- Loading ----
  const fetchProfile = async () => {
    const data = await apiGet('/auth/me');
    setProfile(data);
    setProfileForm({
      email: data.email || '',
      full_name: data.full_name || '',
      organization: data.organization || ''
    });
  };

  const fetchStats = async () => {
    setStats(await apiGet('/auth/admin/stats'));
  };

  const fetchPending = async () => {
    const data = await apiGet('/auth/admin/pending');
    setPending(data.pending || []);
  };

  const fetchUsers = async () => {
    const data = await apiGet('/auth/admin/users?include_disabled=true&limit=200');
    setUsers(data.users || []);
  };

  const fetchWhitelist = async () => {
    setWhitelist(await apiGet('/auth/admin/whitelist'));
  };

  const fetchLogs = async (offset = 0, append = false) => {
    const data = await apiGet(`/auth/admin/audit-log?limit=${LOG_PAGE_SIZE}&offset=${offset}`);
    const newLogs = data.logs || [];
    setLogs(prev => (append ? [...prev, ...newLogs] : newLogs));
    setHasMoreLogs(newLogs.length === LOG_PAGE_SIZE);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const jobs = [fetchProfile()];
      if (isAdmin) {
        jobs.push(fetchStats(), fetchPending(), fetchUsers(), fetchWhitelist(), fetchLogs(0, false));
      }
      await Promise.all(jobs);
    } catch (error) {
      showError(error.message || 'Failed to load settings');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCore = async () => {
    try {
      await Promise.all([fetchStats(), fetchPending(), fetchUsers()]);
    } catch (error) {
      showError(error.message || 'Failed to refresh data');
    }
  };

  const runAction = async (fn, successText) => {
    setBusy(true);
    try {
      await fn();
      showSuccess(successText);
      await refreshCore();
    } catch (error) {
      showError(error.message || 'Action failed');
    }
    setBusy(false);
  };

  // ---- Account actions ----
  const profileChanged = profile && (
    profileForm.email !== (profile.email || '') ||
    profileForm.full_name !== (profile.full_name || '') ||
    profileForm.organization !== (profile.organization || '')
  );

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await apiSend('/auth/me', {
        method: 'PUT',
        body: {
          email: profileForm.email,
          full_name: profileForm.full_name,
          organization: profileForm.organization
        }
      });
      await fetchProfile();
      showSuccess('Profile updated');
    } catch (error) {
      showError(error.message || 'Failed to update profile');
    }
    setSavingProfile(false);
  };

  const changePassword = async () => {
    if (pwForm.next.length < 8) {
      showError('New password must be at least 8 characters.');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      showError('New passwords do not match.');
      return;
    }
    setChangingPw(true);
    try {
      await apiSend('/auth/change-password', {
        body: { current_password: pwForm.current, new_password: pwForm.next }
      });
      setPwForm({ current: '', next: '', confirm: '' });
      showSuccess('Password changed');
    } catch (error) {
      showError(error.message || 'Failed to change password');
    }
    setChangingPw(false);
  };

  // ---- Demo dataset downloads ----
  // Authenticated blob fetch: a plain <a href> can't send the Bearer token,
  // so /files/demo/* would 401 it.
  const [downloadingDemo, setDownloadingDemo] = useState(null);
  const downloadDemoFile = async (name) => {
    setDownloadingDemo(name);
    try {
      const response = await fetch(`${API_URL}/files/demo/${name}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(await readError(response));
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser time to start the save before releasing the blob
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      showError(error.message || `Failed to download ${name}`);
    }
    setDownloadingDemo(null);
  };

  // ---- Admin actions ----
  const approveRequest = (req) => runAction(
    () => apiSend(`/auth/admin/approve/${req.id}`),
    `Approved ${req.username}`
  );

  const rejectRequest = (req) => setConfirm({
    title: 'Reject Access Request',
    body: `Rejecting "${req.username}" permanently deletes this request. This cannot be undone.`,
    confirmLabel: 'Reject Request',
    action: () => runAction(
      () => apiSend(`/auth/admin/reject/${req.id}`),
      `Rejected ${req.username}`
    )
  });

  const changeRole = (u, role) => runAction(
    () => apiSend(`/auth/admin/users/${u.id}/role`, { body: { role } }),
    `Changed ${u.username}'s role to ${role}`
  );

  const toggleUser = (u) => runAction(
    () => apiSend(`/auth/admin/users/${u.id}/${u.disabled ? 'enable' : 'disable'}`),
    `${u.disabled ? 'Enabled' : 'Disabled'} ${u.username}`
  );

  const deleteUser = (u) => setConfirm({
    title: 'Delete User',
    body: `Delete user "${u.username}"? This permanently removes the account and cannot be undone.`,
    confirmLabel: 'Delete User',
    action: () => runAction(
      () => apiSend(`/auth/admin/users/${u.id}`, { method: 'DELETE' }),
      `Deleted ${u.username}`
    )
  });

  const loadMoreLogs = async () => {
    setLogsLoading(true);
    try {
      await fetchLogs(logs.length, true);
    } catch (error) {
      showError(error.message || 'Failed to load more activity');
    }
    setLogsLoading(false);
  };

  // ---- Whitelist actions ----
  const runWhitelistAction = async (fn, successText) => {
    setBusy(true);
    try {
      const config = await fn();
      setWhitelist(config);
      showSuccess(successText);
    } catch (error) {
      showError(error.message || 'Whitelist update failed');
    }
    setBusy(false);
  };

  const toggleAutoApprove = () => runWhitelistAction(
    () => apiSend('/auth/admin/whitelist/config', {
      method: 'PUT',
      body: { auto_approve_whitelisted: !whitelist.auto_approve_whitelisted }
    }),
    `Auto-approve ${whitelist.auto_approve_whitelisted ? 'disabled' : 'enabled'}`
  );

  const addDomain = () => {
    const domain = newDomain.trim();
    if (!domain) return;
    runWhitelistAction(
      () => apiSend('/auth/admin/whitelist/domains', { body: { domain } }),
      `Whitelisted domain ${domain.replace(/^@/, '').toLowerCase()}`
    ).then(() => setNewDomain(''));
  };

  const removeDomain = (domain) => runWhitelistAction(
    () => apiSend(`/auth/admin/whitelist/domains/${encodeURIComponent(domain)}`, { method: 'DELETE' }),
    `Removed domain ${domain}`
  );

  const addEmail = () => {
    const email = newEmail.trim();
    if (!email) return;
    runWhitelistAction(
      () => apiSend('/auth/admin/whitelist/emails', { body: { email } }),
      `Whitelisted ${email.toLowerCase()}`
    ).then(() => setNewEmail(''));
  };

  const removeEmail = (email) => runWhitelistAction(
    () => apiSend(`/auth/admin/whitelist/emails/${encodeURIComponent(email)}`, { method: 'DELETE' }),
    `Removed ${email}`
  );

  const roleCounts = stats?.users_by_role || {};
  const pendingCount = stats?.pending_users ?? pending.length;

  const tabs = [
    { key: 'account', label: 'Account' },
    ...(isAdmin ? [
      { key: 'users', label: 'Users' },
      { key: 'access', label: 'Access Control', badge: pendingCount },
      { key: 'activity', label: 'Activity' }
    ] : [])
  ];

  // ================= Renderers =================

  const renderAccountTab = () => (
    <div className="stack">
      <div className="card rise-in" style={{ '--i': 0 }}>
        <h2 className="section-title">Profile</h2>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Username</span>
          <span style={styles.infoValue}>{profile?.username || user?.username}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Role</span>
          <span className="badge badge--neutral">
            <RoleIcon role={profile?.role || user?.role} size={14} />
            <span style={{ textTransform: 'capitalize' }}>{profile?.role || user?.role}</span>
          </span>
        </div>

        <div style={styles.formGrid}>
          <label className="field" style={styles.fieldTight}>
            <span className="field__label">Full name</span>
            <input
              className="input"
              value={profileForm.full_name}
              onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
              placeholder="Your full name"
            />
          </label>
          <label className="field" style={styles.fieldTight}>
            <span className="field__label">Organization</span>
            <input
              className="input"
              value={profileForm.organization}
              onChange={(e) => setProfileForm({ ...profileForm, organization: e.target.value })}
              placeholder="Hospital / clinic / company"
            />
          </label>
          <label className="field" style={styles.fieldTight}>
            <span className="field__label">Email</span>
            <input
              className="input"
              type="email"
              value={profileForm.email}
              onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
              placeholder="you@example.com"
            />
          </label>
        </div>
        <div style={styles.formActions}>
          <button
            type="button"
            className="btn btn--primary"
            onClick={saveProfile}
            disabled={!profileChanged || savingProfile}
          >
            {savingProfile ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="card rise-in" style={{ '--i': 1 }}>
        <h2 className="section-title">Security</h2>
        <p className="section-sub">Change the password you use to sign in.</p>
        <div style={styles.formGrid}>
          <label className="field" style={styles.fieldTight}>
            <span className="field__label">Current password</span>
            <input
              className="input"
              type="password"
              value={pwForm.current}
              onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
              autoComplete="current-password"
            />
          </label>
          <label className="field" style={styles.fieldTight}>
            <span className="field__label">New password</span>
            <input
              className="input"
              type="password"
              value={pwForm.next}
              onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </label>
          <label className="field" style={styles.fieldTight}>
            <span className="field__label">Confirm new password</span>
            <input
              className="input"
              type="password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              autoComplete="new-password"
            />
          </label>
        </div>
        <div style={styles.formActions}>
          <button
            type="button"
            className="btn btn--primary"
            onClick={changePassword}
            disabled={!pwForm.current || !pwForm.next || changingPw}
          >
            {changingPw ? 'Changing…' : 'Change Password'}
          </button>
        </div>
      </div>

      <div className="card rise-in" style={{ '--i': 2 }}>
        <h2 className="section-title">Try It with Sample Data</h2>
        <p className="section-sub">
          A de-identified demo dataset for exploring the full workflow without
          your own imaging.
        </p>

        <ol style={styles.demoGuide}>
          <li>Download the files below. <strong>Unzip both MRI archives</strong> — the uploader expects DICOM <em>folders</em> for MRI.</li>
          <li>On the <strong>Upload</strong> step, add each file to its matching slot (listed next to every download).</li>
          <li>Click <strong>Proceed to Analysis</strong>. CT processing takes a few minutes; progress is shown while it runs.</li>
          <li>Explore <strong>Measurements</strong>, <strong>Alignment</strong>, and <strong>Planning</strong>, then save the case from <strong>Scan History</strong>.</li>
        </ol>

        <div style={styles.demoList}>
          {DEMO_DOWNLOADS.map(item => (
            <div key={item.name} style={styles.demoRow}>
              <div style={styles.demoRowText}>
                <div style={styles.demoRowTitle}>{item.title}</div>
                <div style={styles.demoRowMeta}>
                  {item.size} · goes in <strong>{item.slot}</strong>
                </div>
              </div>
              <button
                type="button"
                className="icon-btn"
                style={{ flexShrink: 0 }}
                onClick={() => downloadDemoFile(item.name)}
                disabled={downloadingDemo === item.name}
                aria-label={`Download ${item.title}`}
                title="Download"
              >
                {downloadingDemo === item.name ? (
                  <span className="spinner spinner--sm" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M8 2.5v7.5" />
                    <path d="M4.8 6.9L8 10.1l3.2-3.2" />
                    <path d="M2.8 13.5h10.4" />
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

    </div>
  );

  const renderUsersTab = () => (
    <div className="stack">
      {stats && (
        <div style={styles.statsRow}>
          <div className="card card--tight rise-in" style={{ ...styles.statCard, '--i': 0 }}>
            <div style={styles.statValue}>{stats.total_users}</div>
            <div style={styles.statLabel}>Total Users</div>
          </div>
          <div className="card card--tight rise-in" style={{ ...styles.statCard, '--i': 1 }}>
            <div style={{ ...styles.statValue, color: 'var(--success)' }}>
              {stats.active_users}
            </div>
            <div style={styles.statLabel}>Active</div>
          </div>
          <div
            className="card card--tight rise-in"
            style={{
              ...styles.statCard,
              '--i': 2,
              ...(pendingCount > 0 ? styles.statCardWarning : {})
            }}
          >
            <div style={{
              ...styles.statValue,
              ...(pendingCount > 0 ? { color: 'var(--warning)' } : {})
            }}>
              {stats.pending_users}
            </div>
            <div style={styles.statLabel}>Pending Requests</div>
          </div>
          <div className="card card--tight rise-in" style={{ ...styles.statCard, '--i': 3, flex: 1.5 }}>
            <div style={styles.roleCounts}>
              {ROLES.map(role => (
                <div key={role} style={styles.roleCount}>
                  <span style={styles.roleCountValue}>{roleCounts[role] || 0}</span>
                  <span style={styles.roleCountLabel}>{role}</span>
                </div>
              ))}
            </div>
            <div style={styles.statLabel}>By Role</div>
          </div>
        </div>
      )}

      <div className="card">
        <h2 className="section-title" style={styles.cardHeading}>Users ({users.length})</h2>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Name / Organization</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isSelf = u.username === user?.username;
                return (
                  <tr key={u.id} style={u.disabled ? styles.disabledRow : undefined}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <RoleIcon role={u.role} size={13} />
                      <span style={{ fontWeight: 600, marginLeft: '6px' }}>
                        {u.username}
                      </span>
                      {isSelf && <span className="badge badge--accent" style={styles.youBadge}>you</span>}
                    </td>
                    <td>{u.email || '—'}</td>
                    <td>
                      {[u.full_name, u.organization].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td>
                      <select
                        className="select"
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        disabled={isSelf || busy}
                        style={styles.roleSelect}
                      >
                        {ROLES.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className={`badge ${u.disabled ? 'badge--danger' : 'badge--success'}`}>
                        {u.disabled ? 'Disabled' : 'Active'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(u.last_login)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(u.created_at)}</td>
                    <td>
                      <div style={styles.rowActions}>
                        <button
                          type="button"
                          className={`btn btn--sm ${u.disabled ? 'btn--success' : 'btn--secondary'}`}
                          style={u.disabled ? undefined : styles.warnText}
                          onClick={() => toggleUser(u)}
                          disabled={isSelf || busy}
                        >
                          {u.disabled ? 'Enable' : 'Disable'}
                        </button>
                        <button
                          type="button"
                          className="btn btn--sm btn--danger"
                          onClick={() => deleteUser(u)}
                          disabled={isSelf || busy}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <span style={styles.emptyText}>No users found.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAccessTab = () => (
    <div className="stack">
      {pendingCount > 0 && (
        <div className="alert alert--info">
          <span style={styles.noteIcon}>i</span>
          <span>
            Admins also receive an email notification for each new access request.
          </span>
        </div>
      )}

      {/* Pending Access Requests */}
      <div
        className="card"
        style={pending.length > 0 ? { borderColor: 'var(--warning)' } : undefined}
      >
        <h2 className="section-title" style={styles.cardHeading}>
          Pending Access Requests{pending.length > 0 ? ` (${pending.length})` : ''}
        </h2>
        {pending.length === 0 ? (
          <p style={styles.emptyText}>No pending requests.</p>
        ) : (
          <div style={styles.requestList}>
            {pending.map((req, i) => (
              <div key={req.id} className="panel rise-in" style={{ ...styles.requestCard, '--i': i }}>
                <div style={styles.requestInfo}>
                  <div style={styles.requestName}>
                    {req.username}
                    <span style={styles.requestEmail}>{req.email}</span>
                    {req.is_whitelisted && (
                      <span className="badge badge--accent">Whitelisted</span>
                    )}
                  </div>
                  {(req.full_name || req.organization) && (
                    <div style={styles.requestDetail}>
                      {[req.full_name, req.organization].filter(Boolean).join(' — ')}
                    </div>
                  )}
                  <div style={styles.reasonBox}>
                    <span style={styles.reasonLabel}>Reason for access</span>
                    <div style={styles.reasonText}>
                      {req.reason || 'No reason provided.'}
                    </div>
                  </div>
                  <div style={styles.requestDetail}>
                    Requested: {formatDateTime(req.created_at)}
                  </div>
                </div>
                <div style={styles.requestActions}>
                  <button
                    type="button"
                    className="btn btn--success"
                    onClick={() => approveRequest(req)}
                    disabled={busy}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger"
                    onClick={() => rejectRequest(req)}
                    disabled={busy}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Registration Whitelist */}
      <div className="card">
        <h2 className="section-title">Registration Whitelist</h2>
        <p className="section-sub" style={{ marginBottom: 'var(--space-4)' }}>
          Requests from whitelisted domains or email addresses skip manual review
          and are approved the moment they register (when auto-approve is on).
        </p>

        {whitelist ? (
          <>
            <label className="panel" style={styles.toggleRow}>
              <input
                type="checkbox"
                checked={whitelist.auto_approve_whitelisted}
                onChange={toggleAutoApprove}
                disabled={busy}
                style={styles.checkbox}
              />
              <span>
                <span style={styles.toggleLabel}>Auto-approve whitelisted registrations</span>
                <span style={styles.toggleHint}>
                  When off, whitelisted requesters still appear below flagged as
                  “Whitelisted” but wait for manual approval.
                </span>
              </span>
            </label>

            <div style={styles.whitelistSection}>
              <h3 style={styles.whitelistHeading}>Whitelisted Domains</h3>
              <p style={styles.descriptionSmall}>
                Anyone registering with an email at these domains (e.g. hospital.org).
              </p>
              <div style={styles.addRow}>
                <input
                  className="input"
                  style={{ flex: 1 }}
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                  placeholder="domain.com"
                />
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={addDomain}
                  disabled={busy || !newDomain.trim()}
                >
                  Add Domain
                </button>
              </div>
              <div style={styles.tagList}>
                {whitelist.domains.map(domain => (
                  <span key={domain} style={styles.tag}>
                    @{domain}
                    <button
                      type="button"
                      style={styles.tagRemove}
                      onClick={() => removeDomain(domain)}
                      disabled={busy}
                      title={`Remove ${domain}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {whitelist.domains.length === 0 && (
                  <span style={styles.emptyText}>No domains whitelisted.</span>
                )}
              </div>
            </div>

            <div style={styles.whitelistSection}>
              <h3 style={styles.whitelistHeading}>Whitelisted Emails</h3>
              <p style={styles.descriptionSmall}>
                Specific addresses that are pre-approved.
              </p>
              <div style={styles.addRow}>
                <input
                  className="input"
                  style={{ flex: 1 }}
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addEmail()}
                  placeholder="person@example.com"
                />
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={addEmail}
                  disabled={busy || !newEmail.trim()}
                >
                  Add Email
                </button>
              </div>
              <div style={styles.tagList}>
                {whitelist.emails.map(email => (
                  <span key={email} style={styles.tag}>
                    {email}
                    <button
                      type="button"
                      style={styles.tagRemove}
                      onClick={() => removeEmail(email)}
                      disabled={busy}
                      title={`Remove ${email}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {whitelist.emails.length === 0 && (
                  <span style={styles.emptyText}>No emails whitelisted.</span>
                )}
              </div>
            </div>
          </>
        ) : (
          <p style={styles.emptyText}>Whitelist unavailable.</p>
        )}
      </div>
    </div>
  );

  const renderActivityTab = () => (
    <div className="card">
      <h2 className="section-title" style={styles.cardHeading}>Recent Activity</h2>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Action</th>
              <th>Status</th>
              <th>IP Address</th>
              <th>Resource / Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => (
              <tr key={log.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {formatDateTime(log.created_at)}
                </td>
                <td>{log.action}</td>
                <td>
                  <span className={`badge ${log.status === 'success' ? 'badge--success' : 'badge--danger'}`}>
                    {log.status}
                  </span>
                </td>
                <td>{log.ip_address || '—'}</td>
                <td style={styles.detailsCell} title={condenseLog(log)}>
                  {condenseLog(log)}
                </td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr>
                <td colSpan={5}>
                  <span style={styles.emptyText}>No activity recorded.</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {hasMoreLogs && (
        <div style={styles.loadMoreRow}>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={loadMoreLogs}
            disabled={logsLoading}
          >
            {logsLoading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="page">
      {/* Header row */}
      <div className="row row--between" style={styles.headerRow}>
        <h1 style={styles.pageTitle}>Settings</h1>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={loadAll}
          disabled={loading || busy}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Tabs */}
      {tabs.length > 1 && (
        <div className="seg" style={styles.tabs}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              className={`seg__opt${activeTab === tab.key ? ' seg__opt--active' : ''}`}
              style={styles.tabOpt}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className="badge badge--warning" style={styles.tabBadge}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Message banner */}
      {message.text && (
        <div
          className={`alert ${message.type === 'error' ? 'alert--danger' : 'alert--success'}`}
          style={styles.message}
        >
          <span>{message.text}</span>
          <button
            type="button"
            onClick={() => setMessage({ type: '', text: '' })}
            style={styles.closeBtn}
            aria-label="Dismiss message"
          >
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div style={styles.loadingContainer}>
          <div className="spinner"></div>
          <span style={styles.loadingText}>Loading settings…</span>
        </div>
      ) : (
        <>
          {activeTab === 'account' && renderAccountTab()}
          {isAdmin && activeTab === 'users' && renderUsersTab()}
          {isAdmin && activeTab === 'access' && renderAccessTab()}
          {isAdmin && activeTab === 'activity' && renderActivityTab()}
        </>
      )}

      {/* Confirm modal */}
      {confirm && (
        <div className="modal-scrim">
          <div className="modal" style={styles.confirmModal}>
            <div className="modal__header">
              <h3 className="modal__title">{confirm.title}</h3>
            </div>
            <div className="modal__body">
              <p style={styles.confirmBody}>{confirm.body}</p>
            </div>
            <div className="modal__footer">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setConfirm(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger-solid"
                onClick={() => {
                  const action = confirm.action;
                  setConfirm(null);
                  action();
                }}
                disabled={busy}
              >
                {confirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Static layout / decorative styles built on the global design tokens.
// Interactive states (hover/focus/active/disabled) come from the CSS classes.
const styles = {
  headerRow: {
    marginBottom: 'var(--space-4)'
  },
  pageTitle: {
    fontSize: '1.6rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
    margin: 0
  },
  tabs: {
    marginBottom: 'var(--space-5)'
  },
  tabOpt: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  tabBadge: {
    height: '18px',
    padding: '0 7px',
    fontSize: '0.68rem'
  },
  message: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 'var(--space-4)'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    fontSize: '18px',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 4px',
    flexShrink: 0
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-4)',
    padding: '80px 0'
  },
  loadingText: {
    color: 'var(--text-secondary)',
    fontSize: '0.875rem'
  },
  // ---- Account ----
  infoRow: {
    display: 'flex',
    gap: 'var(--space-3)',
    alignItems: 'center',
    marginTop: 'var(--space-3)'
  },
  infoLabel: {
    color: 'var(--text-secondary)',
    minWidth: '100px',
    fontSize: '0.85rem'
  },
  infoValue: {
    color: 'var(--text-primary)',
    fontWeight: 600
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
    gap: 'var(--space-4)',
    marginTop: 'var(--space-4)'
  },
  fieldTight: {
    margin: 0
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 'var(--space-4)'
  },
  // ---- Demo dataset ----
  demoGuide: {
    margin: 'var(--space-3) 0 var(--space-4)',
    paddingLeft: '22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '0.86rem',
    lineHeight: 1.5,
    color: 'var(--text-secondary)',
    maxWidth: '68ch'
  },
  demoList: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid var(--line-subtle)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden'
  },
  demoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-3)',
    padding: '10px 14px',
    borderBottom: '1px solid var(--line-subtle)',
    background: 'var(--bg-surface)'
  },
  demoRowText: {
    minWidth: 0
  },
  demoRowTitle: {
    fontSize: '0.88rem',
    fontWeight: 600,
    color: 'var(--text-primary)'
  },
  demoRowMeta: {
    fontSize: '0.78rem',
    color: 'var(--text-tertiary)',
    marginTop: '2px'
  },
  // ---- Whitelist ----
  toggleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-3)',
    cursor: 'pointer',
    marginBottom: 'var(--space-4)'
  },
  checkbox: {
    marginTop: '3px',
    width: '16px',
    height: '16px',
    accentColor: 'var(--accent)',
    cursor: 'pointer',
    flexShrink: 0
  },
  toggleLabel: {
    display: 'block',
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
    fontWeight: 600
  },
  toggleHint: {
    display: 'block',
    color: 'var(--text-tertiary)',
    fontSize: '0.8rem',
    marginTop: '2px'
  },
  whitelistSection: {
    marginTop: 'var(--space-5)'
  },
  whitelistHeading: {
    fontSize: '0.95rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
    margin: 0
  },
  descriptionSmall: {
    color: 'var(--text-tertiary)',
    fontSize: '0.8rem',
    margin: '4px 0 10px 0'
  },
  addRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--space-2)',
    marginBottom: 'var(--space-3)',
    maxWidth: 'min(480px, 100%)'
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--space-2)'
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'var(--accent-soft)',
    color: 'var(--accent-hover)',
    padding: '4px 6px 4px 12px',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.82rem',
    fontWeight: 500
  },
  tagRemove: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    fontSize: '15px',
    cursor: 'pointer',
    lineHeight: 1,
    padding: '2px 6px',
    borderRadius: 'var(--radius-full)'
  },
  // ---- Admin ----
  cardHeading: {
    marginBottom: 'var(--space-3)'
  },
  statsRow: {
    display: 'flex',
    gap: 'var(--space-4)',
    flexWrap: 'wrap'
  },
  statCard: {
    flex: 1,
    minWidth: 'min(160px, 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 'var(--space-2)'
  },
  statCardWarning: {
    borderColor: 'var(--warning)',
    background: 'var(--warning-soft)'
  },
  statValue: {
    fontSize: '1.9rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
    lineHeight: 1
  },
  statLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: 'var(--text-secondary)'
  },
  roleCounts: {
    display: 'flex',
    gap: 'var(--space-4)',
    flexWrap: 'wrap'
  },
  roleCount: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  roleCountValue: {
    fontSize: '1.2rem',
    fontWeight: 600,
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
    lineHeight: 1.2
  },
  roleCountLabel: {
    fontSize: '0.72rem',
    color: 'var(--text-tertiary)',
    textTransform: 'capitalize'
  },
  noteIcon: {
    background: 'var(--accent)',
    color: '#fff',
    borderRadius: 'var(--radius-full)',
    width: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 700,
    flexShrink: 0,
    marginTop: '2px'
  },
  emptyText: {
    color: 'var(--text-tertiary)',
    fontStyle: 'italic',
    fontSize: '0.85rem'
  },
  requestList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-3)',
    marginTop: 'var(--space-2)'
  },
  requestCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 'var(--space-4)'
  },
  requestInfo: {
    flex: 1,
    minWidth: 'min(260px, 100%)'
  },
  requestName: {
    fontSize: '1rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    flexWrap: 'wrap'
  },
  requestEmail: {
    fontSize: '0.85rem',
    fontWeight: 400,
    color: 'var(--text-secondary)'
  },
  requestDetail: {
    fontSize: '0.8rem',
    color: 'var(--text-tertiary)',
    marginTop: '6px'
  },
  reasonBox: {
    marginTop: '10px',
    padding: '10px 12px',
    background: 'var(--accent-softer)',
    borderLeft: '3px solid var(--accent)',
    borderRadius: 'var(--radius-sm)'
  },
  reasonLabel: {
    display: 'block',
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
    marginBottom: '4px',
    fontWeight: 600
  },
  reasonText: {
    fontSize: '0.9rem',
    color: 'var(--text-primary)',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap'
  },
  requestActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-2)',
    flexShrink: 0
  },
  disabledRow: {
    opacity: 0.55
  },
  youBadge: {
    height: '18px',
    padding: '0 7px',
    fontSize: '0.62rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginLeft: '8px'
  },
  roleSelect: {
    width: 'auto',
    minWidth: '118px',
    height: '32px',
    fontSize: '0.8rem'
  },
  warnText: {
    color: 'var(--warning)'
  },
  rowActions: {
    display: 'flex',
    gap: 'var(--space-2)'
  },
  detailsCell: {
    color: 'var(--text-secondary)',
    maxWidth: '360px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  loadMoreRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 'var(--space-4)'
  },
  // ---- Confirm modal ----
  confirmModal: {
    maxWidth: '440px'
  },
  confirmBody: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
    margin: 0
  }
};

export default SettingsPage;
